import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import MuiCheckbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from '@mui/material/Grid';
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { observer, inject } from "mobx-react";
import { dreamUser, dreamUsers, dreamUserWithRoles, getCurrentOrg, api2AdminRoles, api2Admin, api2AdminAdditionalOrganizations } from "../../utils/operations.js";
import API from '../../utils/api';
import { toast } from 'react-toastify';
import { spatieRoleNameToDreamId } from "../../utils/misc";

const checkEmail = email => {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

const EditAdmin = ({ Auth, history, match }) => {
  const [admin, setAdmin] = useState({
    name: "", organisation_id: "", email: "", password: "",
    show_finance: false, edit_finance: false, show_charts: false, show_future_invoices: false,
  });
  const [userData, setUserData] = useState({ phone: 'admin' });
  const [roleId, setRoleId] = useState('');
  const [roles, setRoles] = useState([]);
  const [adminOrganisations, setAdminOrganisations] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [textErrors, setTextErrors] = useState([]);
  const [showProgress, setShowProgress] = useState(false); // eslint-disable-line no-unused-vars

  const isPossibleAdditionalSchools = useMemo(
    () => ['admin', 'superadmin'].includes(roles.find(r => r.id === roleId)?.name),
    [roles, roleId]
  );

  useEffect(() => {
    if (!Auth.can('administrators')) { history.push("/children"); return; }
    const userId = match.params.id;
    axios.all([
      API.get(dreamUserWithRoles(userId)),
      API.get(api2Admin(userId)),
      API.get(getCurrentOrg),
      API.get(api2AdminAdditionalOrganizations(userId)),
      API.get(api2AdminRoles),
    ]).then(axios.spread((user, adminRes, orgs, adminOrgs, rolesRes) => {
      setUserData(user.data);
      const a = adminRes.data.admin;
      setAdmin({ ...a, password: '' });
      setOrganisations(orgs.data.resource);
      setAdminOrganisations(adminOrgs.data.organization_id);
      setRoles(rolesRes.data.roles || []);
      setRoleId(a.role_id ? Number(a.role_id) : '');
    }));
  }, []);

  const handleChange = ({ target: { id, value } }) => setAdmin(prev => ({ ...prev, [id]: value }));

  const handleRoleChange = e => {
    if (Auth.role !== 'superadmin') return;
    setRoleId(e.target.value);
  };

  const handlePermissionsToggle = name => {
    if (Auth.role !== 'superadmin') return;
    setAdmin(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const checkInfo = () => {
    const errors = [];
    if (!admin.name) errors.push(<span key="name">* Name cannot be empty</span>);
    if (!checkEmail(admin.email)) errors.push(<span key="email">* Email must be valid</span>);
    if (admin.password !== '' && admin.password.length < 6) errors.push(<span key="pw">* Password length must be at least 6 symbols</span>);
    if (errors.length > 0) { setTextErrors(errors); return false; }
    return true;
  };

  const deleteData = async () => {
    setOpenDialog(false);
    try {
      await API.delete(dreamUsers + "/" + userData.id);
      await API.delete(api2Admin(admin.id));
      toast.success('Administrator deleted successfully!');
      history.push("/admins");
    } catch (error) {
      toast.error(error);
    }
  };

  const saveData = async () => {
    const user_id = userData.id;
    const selectedRoleName = roles.find(r => r.id === roleId)?.name || 'admin';
    const dreamRoleId = spatieRoleNameToDreamId(selectedRoleName);
    const uData = {
      name: admin.name, first_name: admin.name, last_name: admin.name,
      is_active: true, email: admin.email, username: admin.email,
      phone: selectedRoleName
    };
    if (admin.password !== '') uData['password'] = admin.password;

    const userRoleData = (userData.user_to_app_to_role_by_user_id || []).map(r => ({ ...r, role_id: dreamRoleId }));
    const adminData = {
      name: admin.name, organisation_id: admin.organisation_id, email: admin.email,
      show_finance: admin.show_finance, edit_finance: admin.edit_finance,
      show_charts: admin.show_charts, show_future_invoices: admin.show_future_invoices,
      role_id: roleId || null,
    };
    if (admin.password !== '') adminData['password'] = admin.password;
    const extra = adminOrganisations.filter(id => id !== admin.organisation_id);

    try {
      await API.post(api2AdminAdditionalOrganizations(admin.id), extra);
      await API.patch(api2Admin(admin.id), adminData);
      await API.patch(dreamUser(user_id), uData);
      await API.put(dreamUserWithRoles(user_id), { user_to_app_to_role_by_user_id: userRoleData });
      toast.success('Administrator edited successfully!');
      setTimeout(() => history.push("/admins"), 1000);
    } catch (error) {
      const data = error.response?.data;
      const errText = data?._issues
        ? Object.keys(data._issues).map(k => `${k}: ${data._issues[k]}`).join('\n')
        : (data?.error || data?.message || error.message || 'An error occurred');
      setTextErrors([errText]);
      setOpenDialog(true);
    }
  };

  const saveInfo = () => {
    if (checkInfo()) saveData();
    else setOpenDialog(true);
  };

  return (
    <div style={{ width: 500, margin: "auto" }}>
      {showProgress && <LinearProgress sx={{ mb: 1 }} />}
      <br />
      <TextField id="name" label="Name" fullWidth value={admin.name} onChange={handleChange} sx={{ mb: 2 }} />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="role-label">Role</InputLabel>
        <Select labelId="role-label" label="Role" value={roleId} onChange={handleRoleChange}>
          {roles.map(r => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="org-label">School</InputLabel>
        <Select labelId="org-label" label="School" value={admin.organisation_id || ''} onChange={({ target: { value } }) => setAdmin(prev => ({ ...prev, organisation_id: value }))}>
          {organisations.map(row => <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>)}
        </Select>
      </FormControl>
      <TextField id="email" label="Email" fullWidth value={admin.email} onChange={handleChange} sx={{ mb: 2 }} />
      <TextField type="password" id="password" label="Password" fullWidth value={admin.password} onChange={handleChange} sx={{ mb: 2 }} />
      {roles.find(r => r.id === roleId)?.name !== 'superadmin' && (
        <Grid container spacing={3} style={{ marginTop: '20px' }}>
          <Grid item xs={6}>
            <FormControlLabel control={<MuiCheckbox checked={!!admin.show_finance} onChange={() => handlePermissionsToggle('show_finance')} />} label="Show Finance Info" />
            <FormControlLabel control={<MuiCheckbox checked={!!admin.edit_finance} onChange={() => handlePermissionsToggle('edit_finance')} />} label="Edit Finance" />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel control={<MuiCheckbox checked={!!admin.show_future_invoices} onChange={() => handlePermissionsToggle('show_future_invoices')} />} label="View Future Invoices" />
            <FormControlLabel control={<MuiCheckbox checked={!!admin.show_charts} onChange={() => handlePermissionsToggle('show_charts')} />} label="View Financial Charts" />
          </Grid>
        </Grid>
      )}
      {isPossibleAdditionalSchools && (
        <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
          <InputLabel id="add-orgs-label">Additional schools</InputLabel>
          <Select labelId="add-orgs-label" label="Additional schools" multiple value={adminOrganisations} onChange={e => setAdminOrganisations(e.target.value)}>
            {organisations.filter(o => o.id !== admin.organisation_id).map(row => (
              <MenuItem key={row.id} value={row.id}>{row.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <div style={{ display: 'flex', gap: 10, paddingTop: 20, paddingBottom: 20 }}>
        <Button variant="contained" color="primary" onClick={saveInfo}>Save</Button>
        <Button variant="contained" sx={{ backgroundColor: "#ffd699", color: "#000", "&:hover": { backgroundColor: "#ffcc77" } }} onClick={() => history.push("/admins")}>Cancel</Button>
        <Button variant="contained" color="error" onClick={() => setOpenDialog(true)}>Delete</Button>
      </div>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogContent>{textErrors.length > 0 ? textErrors : 'Delete administrator?'}</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          {textErrors.length === 0 && <Button onClick={deleteData} color="primary">OK</Button>}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default inject("Auth")(observer(EditAdmin));

